export const MODULES = [
  "contacts",
  "products",
  "warehouses",
  "stock",
  "price_lists",
  "payment_terms",
  "sellers",
  "statuses",
  "quotes",
  "sales_orders",
  "receivables",
  "payables",
  "financial_accounts",
  "financial_categories",
  "payment_methods",
  "payment_integrations",
  "commissions",
  "checkout",
  "dashboard",
  "goals",
  "copilot",
  "marketplaces",
  "fiscal_certificates",
  "tax_operations",
  "invoices",
  "inbound_invoices",
  "purchase_orders",
  "production_orders",
  "grouped_billing",
  "contracts",
  "service_orders",
  "cnab",
  "gnre",
  "pos",
  "logistics",
  "custom_reports",
  "label_templates",
  "accounting",
  "api_integrations",
  "ai_agents",
  "custom_fields",
  "attachments",
  "jobs",
  "deletions",
  "audit",
  "users",
  "companies",
] as const;
export const ACTIONS = [
  "read",
  "create",
  "update",
  "delete",
  "restore",
  "export",
  "read_all",
  "read_cost",
  "settle_discount",
  "pay",
] as const;
export type Module = (typeof MODULES)[number];
export type Action = (typeof ACTIONS)[number];
export type Permissions = Partial<Record<Module, Action[]>>;

/**
 * Ações que fazem sentido em cada módulo. `export` = exportação em massa (separada de `read`, SEG6);
 * `jobs.read_all` = ver ações assíncronas de outros usuários (decisão 11);
 * `products.read_cost` = ver custo (preço de custo, custo médio e último custo do fornecedor) na tela, na API e no CSV,
 * inclusive o custo médio, o valor do estoque e o custo dos movimentos nas telas de estoque;
 * `stock.create` = lançar movimento, transferir e estornar; `stock.update` = configurações do estoque (negativo).
 */
export const MODULE_ACTIONS: { readonly [M in Module]: readonly Action[] } = {
  contacts: ["read", "create", "update", "delete", "export"],
  /** Imagens do produto exigem `update` (fazem parte do cadastro). */
  products: ["read", "create", "update", "delete", "export", "read_cost"],
  warehouses: ["read", "create", "update", "delete"],
  stock: ["read", "create", "update"],
  /** Lista com base "custo" exige também products:read_cost (a simulação revelaria o custo). */
  price_lists: ["read", "create", "update", "delete"],
  /** `update` também liga a prorrogação de vencimentos para o próximo dia útil (configuração da empresa). */
  payment_terms: ["read", "create", "update", "delete"],
  /**
   * Vendedores e regras de comissão (RF-CAD-05). Vincular o usuário de acesso exige também users:update (e ver os
   * usuários, users:read).
   */
  sellers: ["read", "create", "update", "delete"],
  /**
   * Situações e Gerenciador de transições (RF-PRF-03): `read` para ver as situações nas listas de documentos; criar,
   * editar e excluir situações customizadas e transições com ações (reservar, lançar e estornar estoque e contas).
   */
  statuses: ["read", "create", "update", "delete"],
  /**
   * Propostas comerciais (RF-VEN-01): `update` também muda a situação e converte em pedido; `read` também baixa o PDF.
   */
  quotes: ["read", "create", "update", "delete"],
  /**
   * Pedidos de venda (RF-VEN-02): `update` também muda a situação (com as ações da transição), lança e estorna estoque e
   * contas do pedido e muda situação em lote; `create` também duplica e converte proposta em pedido; `read` também
   * imprime (PDF, inclusive em lote).
   */
  sales_orders: ["read", "create", "update", "delete"],
  /**
   * Contas a receber e a pagar (RF-FIN-01): `update` também baixa (inclusive em lote), estorna baixa e cancela. Título
   * não se exclui (cancela). `settle_discount` (além de `update`) = baixa com desconto ou sem dinheiro na conta
   * (desconto total ou, a receber, taxa igual ao recebido): quita o título sem entrada de caixa.
   */
  receivables: ["read", "create", "update", "settle_discount"],
  payables: ["read", "create", "update", "settle_discount"],
  /**
   * Caixas e bancos (RF-FIN-02): `read` = saldos, extrato e transferências; `create` = cadastrar conta, lançar,
   * transferir e estornar lançamento; `update` = editar a conta; `delete` = excluir conta sem lançamentos.
   */
  financial_accounts: ["read", "create", "update", "delete"],
  /** Categorias financeiras / plano de contas da DRE (RF-FIN-06). */
  financial_categories: ["read", "create", "update", "delete"],
  /** Formas de pagamento (RF-FIN-07). */
  payment_methods: ["read", "create", "update", "delete"],
  /**
   * Integrações de pagamento (T1.10): `read` = ver a configuração Pix (sem credenciais); `update` = configurar provedor,
   * chave, conta de destino e credenciais. Gerar, cancelar e consultar cobranças Pix usam receivables:update.
   */
  payment_integrations: ["read", "update"],
  /**
   * Conta-corrente de comissões (T1.11, RF-VEN-08). Ler o resumo e o extrato usa sellers:read (o usuário-vendedor vê só
   * o dele). `create` = lançar outro crédito/débito e estornar; `pay` = lançar pagamento (gera conta a pagar: exige
   * também payables:create) e estornar pagamento (cancela a conta: exige também payables:update).
   */
  commissions: ["create", "pay"],
  /**
   * Checkout de pedidos (T1.12, RF-VEN-07): `read` = fila, conferência, relatório de separação e tempo real (WebSocket);
   * `create` = abrir, bipar, reiniciar, salvar (muda a situação do pedido para a da configuração, sem exigir
   * sales_orders:update) e cancelar a conferência; `update` = configurar as situações do checkout.
   */
  checkout: ["read", "create", "update"],
  /**
   * Dashboards do Meu Negócio (T1.13, RF-BI-01..09): `read` = Mensal, Diário, anual, pedidos por UF/situação/produto/
   * canal e vendedor/canal 24 meses (o ranking por vendedor exige também sellers:read; custo, margem e lucro exigem
   * products:read_cost); `update` = situações que contam como venda, imposto e taxa de venda. Atenção ao conceder:
   * `read` sozinho já mostra os nomes e SKUs dos produtos vendidos (Top SKUs, por produto) e os totais de venda da
   * empresa (faturamento, pedidos, ticket, DRE sem custo, canais e UFs), sem precisar de products:read nem de
   * sales_orders:read.
   */
  dashboard: ["read", "update"],
  /**
   * Metas (RF-BI-05): o card "Metas em andamento" do Mensal usa `read`. Atenção ao conceder: `read` mostra o
   * realizado de cada meta, ou seja, o faturamento (pedidos, itens, ticket, novos clientes) da empresa no período da
   * meta, mesmo sem dashboard:read. Metas por vendedor (nome e realizado do vendedor) só com sellers:read.
   */
  goals: ["read", "create", "update", "delete"],
  /**
   * Copiloto (T1.14, 06-ia-agentes): `read` = perguntar no painel e pelo WhatsApp e cadastrar o próprio número. Só
   * leitura: cada ferramenta exige também a permissão do módulo que consulta (dashboard, contatos, produtos, pedidos,
   * contas...), com a carteira do usuário-vendedor; custo só com products:read_cost.
   */
  copilot: ["read"],
  /**
   * Marketplaces (T1.16, RF-VEN-09: Mercado Livre): `read` = contas, anúncios e pedidos importados; `create` = conectar
   * conta; `update` = configuração da conta, vincular anúncios, importar anúncios, sincronizar, reprocessar pedidos;
   * `delete` = desconectar. Ligar a sincronização de preço exige também products:update (lista de custo, também
   * products:read_cost) e a de estoque, stock:read.
   */
  marketplaces: ["read", "create", "update", "delete"],
  /**
   * Certificado digital A1 (T2.1, RF-PRF-08): `read` = CNPJ da empresa, certificado ativo (titular, emissor, validade,
   * impressão digital) e histórico, nunca o arquivo nem a senha; `update` = enviar ou substituir o certificado. Gravar o
   * CNPJ da empresa usa companies:update. Só o Administrador por padrão.
   */
  fiscal_certificates: ["read", "update"],
  /**
   * Naturezas de operação e regras de tributação (T2.2, RF-VEN-04): `read` = ver naturezas e regras e simular impostos
   * (painel "Impostos estimados" do pedido e da proposta); `create` = criar natureza (e, com companies:update, rodar o
   * assistente, que grava o perfil fiscal da empresa); `update` = editar, inativar/reativar, marcar padrão e criar ou
   * editar regras. Sem `delete`: natureza e regra se inativam.
   */
  tax_operations: ["read", "create", "update"],
  /**
   * Notas fiscais eletrônicas (T2.3, RF-VEN-03): `read` = lista, detalhe, DANFE e XML (usuário-vendedor só das notas
   * dos pedidos da carteira); `create` = emitir (do pedido ou avulsa, enviar e reenviar); `update` = editar rascunho ou
   * rejeitada e a configuração da NF-e (série, próximo número, e-mail; o ambiente exige também companies:update).
   * Sem `delete`: nota não se exclui (05 §10).
   */
  invoices: ["read", "create", "update"],
  /**
   * Notas de entrada (T2.5, RF-SUP-04): `read` = notas recebidas e de compra, XML e sugestões da IA; `create` = enviar
   * XML e lançar a entrada (o estoque exige também stock:create; as contas a pagar, payables:create; o fornecedor novo,
   * contacts:create; o produto novo, products:create); `update` = sincronizar com a SEFAZ (DistDFe), manifestar,
   * configurar, vincular itens, pedir sugestões à IA, aceitar/rejeitar sugestões e estornar a entrada.
   */
  inbound_invoices: ["read", "create", "update"],
  /**
   * Pedidos de compra (T2.6, RF-SUP-03): `read` = lista, pedido, recebimentos e histórico; `create` = incluir; `update`
   * = editar, mudar a situação, receber (o estoque exige também stock:create; as contas a pagar, payables:create),
   * cancelar o saldo, estornar recebimento e vincular a NF-e de entrada ao pedido no lançamento. Sem `delete`: o pedido
   * se cancela (estorno, nunca DELETE).
   */
  purchase_orders: ["read", "create", "update"],
  /**
   * Ordens de produção (T3.2, RF-SUP-05): `read` = lista, ordem, apontamentos e custo apurado (o custo dos insumos
   * exige também products:read_cost); `create` = criar a ordem; `update` = editar, mudar a situação, apontar produção,
   * finalizar e estornar apontamento — o que mexe no estoque exige também stock:create. A estrutura do produto usa
   * products:update. Sem `delete`: a ordem se cancela (estorno dos apontamentos).
   */
  production_orders: ["read", "create", "update"],
  /**
   * Faturamento agrupado (T2.7, RF-FIN-03): `read` = lista, faturamento e pedidos atendidos não faturados; `create` =
   * faturar (a NF-e exige também invoices:create; as contas a receber, receivables:create); `update` = desfazer pelo
   * estorno (com contas, exige receivables:update). Sem `delete`: estorno, nunca DELETE.
   */
  grouped_billing: ["read", "create", "update"],
  /**
   * Contratos e cobranças recorrentes (T2.8, RF-SER-01/02): `create` = cadastrar contrato e gerar cobranças (a conta
   * exige também receivables:create; a NFS-e, invoices:create); `update` = editar o contrato, excluir cobranças por
   * estorno (com receivables:update) e configurar a geração automática. Sem `delete`: contrato encerra (Inativo/Baixado).
   */
  contracts: ["read", "create", "update"],
  /**
   * Ordens de serviço (T2.9, RF-SER-03): `read` = lista, OS, histórico e impressão (OS e comprovante de entrada);
   * `create` = incluir; `update` = editar, mudar a situação (com as ações: baixa das peças e contas) e finalizar. As
   * notas da OS (NFS-e e NF-e) exigem também invoices:create. Sem `delete`: a OS vai para "Não aprovada".
   */
  service_orders: ["read", "create", "update"],
  /**
   * Cobrança bancária por arquivo CNAB (T2.10, RF-FIN-04): `read` = remessas, retornos e baixar o arquivo; `create` =
   * gerar remessa e ler retorno (a baixa das contas exige também receivables:update); `update` = configurar a carteira
   * da conta financeira (convênio, carteira, sequências). Sem `delete`: arquivo gerado/lido fica no histórico.
   */
  cnab: ["read", "create", "update"],
  /**
   * Guias GNRE e DARE-SP (T2.11, RF-VEN-11): `read` = lista, guia, XML do lote, PDF e comprovante; `create` = gerar as
   * guias da NF-e (a conta a pagar exige também payables:create); `update` = vencimento (com payables:update: muda a
   * conta a pagar), emitir (código de barras), pagar (com payables:update), cancelar por estorno (com payables:update), comprovante e configuração (IE de
   * substituto por UF, guia para SP). Sem `delete`: guia errada é estornada.
   */
  gnre: ["read", "create", "update"],
  /**
   * PDV e controle de caixa (T3.1, RF-VEN-05/06): `read` = vendas do PDV, caixas e relatórios, catálogo offline;
   * `create` = vender, abrir/fechar o próprio caixa, suprimento e sangria; `update` = cancelar venda, devolução/troca,
   * terminais, configuração do PDV (limites de desconto, balança, contingência) e fechar o caixa de outro operador.
   * Sem `delete`: venda se cancela por estorno; caixa e movimentos ficam no histórico.
   */
  pos: ["read", "create", "update"],
  /**
   * Logística (T3.4, RF-VEN-10): `read` = integrações (sem credenciais), objetos de postagem, eventos, remessas e
   * etiquetas; `create` = cotar e gerar objeto/etiqueta (o Melhor Envio debita a carteira), reversa e remessa;
   * `update` = cancelar (evento, nunca DELETE), tentar de novo, sincronizar rastreamento, vincular/fechar remessa e
   * configurar/conectar integrações. Sem `delete`: integração se desativa; objeto se cancela.
   */
  logistics: ["read", "create", "update"],
  /**
   * Relatórios customizados (T3.6, RF-CAD-06): `read` = ver os modelos (os próprios e os compartilhados) e rodar a
   * prévia; `create` = salvar modelo; `update` = editar e arquivar os compartilhados (o autor edita os próprios);
   * `export` = exportar CSV/XLSX/PDF. Cada fonte exige também a leitura do módulo dela (pedidos, produtos, estoque,
   * contatos, contas a receber/pagar); custo só com products:read_cost; vendedor e comissão só com sellers:read; o
   * usuário-vendedor vê só a carteira. Sem `delete`: modelo se arquiva.
   */
  custom_reports: ["read", "create", "update", "export"],
  /**
   * Modelos de etiquetas (T3.6, RF-CAD-06): `read` = ver modelos, prévia e imprimir (PDF/ZPL; etiqueta de produto
   * exige também products:read e de contato, contacts:read, na carteira do usuário-vendedor); `create`; `update` =
   * editar e arquivar. Sem `delete`: modelo se arquiva.
   */
  label_templates: ["read", "create", "update"],
  /**
   * Contabilidade (T3.5, RF-CTB-01/02, RF-PRF-10): `read` = arquivos fiscais (SPED Fiscal, SPED Contribuições,
   * Sintegra), pacotes do contador (enviados e recebidos), DAS MEI e configuração, com download; `create` = gerar
   * arquivo fiscal, montar pacote e gerar o DAS (este com payables:create); `update` = configurar o Espaço Meu Contador,
   * enviar/reenviar pacote, anexar recebido, informar o código de barras do DAS, pagar e cancelar o DAS (estes com
   * payables:update). Sem `delete`: nada se exclui.
   */
  accounting: ["read", "create", "update"],
  /**
   * API pública, webhooks e MCP (T3.8, RF-PRF-11): `read` = ver as chaves (nunca o segredo), as assinaturas de webhook,
   * o log de entregas e as propostas do MCP; `create` = criar chave (só com escopos que o próprio usuário tem) e
   * assinatura (só de eventos de módulos que ele lê); `update` = revogar chave, editar/desativar/reativar assinatura,
   * girar o segredo, reenviar entrega e "Enviar teste". Só com sessão (uma chave não gerencia chaves). Sem `delete`:
   * chave se revoga, assinatura se desativa. Só o Administrador por padrão.
   */
  api_integrations: ["read", "create", "update"],
  /**
   * Agentes de IA (T3.7, 06-ia-agentes §3): `read` = ver a tela Agentes de IA (sugestões, execuções, taxa de aceite e
   * configurações); `update` = alterar as configurações (ligar agentes, régua, limites) e "Rodar agora". Cada sugestão
   * exige ainda o módulo do agente para ver e decidir: planejador purchase_orders:read/create + products:read_cost, cobrador
   * receivables:read/update, sentinela dashboard:read (margem também products:read_cost; desconto por vendedor também
   * sellers:read), cadastro inteligente products:create. O usuário-vendedor não acessa os agentes.
   */
  ai_agents: ["read", "update"],
  custom_fields: ["read", "create", "update", "delete"],
  attachments: ["read", "create", "delete"],
  jobs: ["read", "create", "read_all"],
  deletions: ["read", "restore"],
  audit: ["read"],
  users: ["read", "create", "update", "delete"],
  companies: ["read", "create", "update", "delete"],
};

export function can(perms: Permissions, module: Module, action: Action): boolean {
  return perms[module]?.includes(action) ?? false;
}

const isModule = (m: string): m is Module => (MODULES as readonly string[]).includes(m);

/**
 * Lê `roles.permissions` (JSON) mantendo só pares módulo/ação conhecidos.
 * JSON inválido ou formato inesperado resulta em nenhuma permissão (nunca em exceção).
 */
export function parsePermissions(raw: string | null | undefined): Permissions {
  let data: unknown;
  try {
    data = JSON.parse(raw ?? "{}");
  } catch {
    return {};
  }
  if (typeof data !== "object" || data === null || Array.isArray(data)) return {};
  const out: Permissions = {};
  for (const [m, actions] of Object.entries(data)) {
    if (!isModule(m) || !Array.isArray(actions)) continue;
    const allowed = MODULE_ACTIONS[m];
    const valid = [...new Set(actions)].filter((a): a is Action => allowed.includes(a as Action));
    if (valid.length) out[m] = valid;
  }
  return out;
}
